import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/baseHooks';
import { removeAdditionalDataProp, setAdditionalDataTransferProp } from 'src/store/slices/uiFlowSlice';

/**
 * Facility to store data in the UI Flow Context in a key: value format.
 * Useful for sharing specific data between separated components.
 *
 */
export const useAdditionalDataTransferObject = () => {
  const dispatch = useAppDispatch();
  const additionalDataTransferObject = useAppSelector(
    state => state.uiflow.additionalDataState.additionalDataTransferObject
  );

  /**
   * Register a property without a value
   * @param property
   */
  const register = useCallback(
    (property: string) => {
      dispatch(setAdditionalDataTransferProp({ [property]: '' }));
    },
    [dispatch]
  );

  /**
   * Get a property value
   */
  const get = useCallback(
    (property: string) => {
      if (!additionalDataTransferObject.hasOwnProperty(property)) {
        return '';
      }

      return additionalDataTransferObject[property];
    },
    [additionalDataTransferObject]
  );

  /**
   * Set a property value
   *
   * @param property
   * @param data
   */
  const set = useCallback(
    (property: string, data: any) => {
      dispatch(setAdditionalDataTransferProp({ [property]: data }));
    },
    [dispatch]
  );

  /**
   * Remove a property
   */
  const deRegister = useCallback(
    (property: string) => {
      dispatch(removeAdditionalDataProp(property));
    },
    [dispatch]
  );

  return { register, get, set, deRegister };
};
